import { useState, useEffect, useRef } from 'react'
import { Responsive } from 'react-grid-layout'
// @ts-ignore
import type ReactGridLayout from 'react-grid-layout'
import type { WidgetSettings } from '../../stores/monthlyDashboardLayoutStore'
import { useUiStore } from '../../stores/uiStore'
import { KPI_ACCENTS, KPI_NO_COMPARISONS } from '../../lib/dashboardHelpers'
import DelayedLoadingWrapper from './DelayedLoadingWrapper'

export interface WidgetGridProps {
    layouts: Record<string, ReactGridLayout.Layout[]>
    activeWidgets: string[]
    widgetSettings: Record<string, WidgetSettings>
    isEditing: boolean
    layoutVersion: number
    setLayouts: (l: Record<string, ReactGridLayout.Layout[]>) => void
    snapshotLayouts: () => void
    removeWidget: (id: string) => void
    updateWidgetSettings: (id: string, settings: Partial<WidgetSettings>) => void
    renderWidget: (id: string) => React.ReactNode
    settingsOpen: string | null
    setSettingsOpen: (id: string | null) => void
    isFetching?: boolean
}

export default function MonthlyWidgetGrid({
    layouts, activeWidgets, widgetSettings, isEditing, layoutVersion,
    setLayouts, snapshotLayouts, removeWidget,
    updateWidgetSettings, renderWidget, settingsOpen, setSettingsOpen, isFetching = false
}: WidgetGridProps) {

    const forcedLayout = useUiStore(s => s.forcedLayout)
    const [width, setWidth] = useState(1200)
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!containerRef.current) return
        const observer = new ResizeObserver((entries) => {
            if (entries[0]) {
                setWidth(entries[0].contentRect.width)
            }
        })
        observer.observe(containerRef.current)
        return () => observer.disconnect()
    }, [])

    const handleDragStart = () => {
        snapshotLayouts()
    }

    const handleResizeStart = () => {
        snapshotLayouts()
    }

    const handleLayoutChange = (_currentLayout: any, allLayouts: any) => {
        // Only persist layout changes when the user is actively editing
        if (isEditing) {
            setLayouts(allLayouts)
        }
    }

    // If the server layout didn't provide an xs array, react-grid-layout shrinks heights
    // by a factor of 4 while interpolating. Force a safe xs array if missing.
    const guaranteedLayouts = { ...layouts }

    // Ensure every active widget has a layout entry (handles newly added defaults)
    const lgItems = guaranteedLayouts.lg || []
    const lgIds = new Set(lgItems.map((item: any) => item.i))
    let maxLgY = lgItems.reduce((max: number, l: any) => Math.max(max, l.y + l.h), 0)
    const missingLgItems: any[] = []
    activeWidgets.forEach(id => {
        if (!lgIds.has(id)) {
            missingLgItems.push({ i: id, x: 0, y: maxLgY, w: 40, h: 20 })
            maxLgY += 20
        }
    })
    if (missingLgItems.length > 0) {
        guaranteedLayouts.lg = [...lgItems, ...missingLgItems]
    }

    if (!guaranteedLayouts.xs && guaranteedLayouts.lg) {
        let currentY = 0
        let currentX = 0
        guaranteedLayouts.xs = guaranteedLayouts.lg.map((item: any) => {
            // Reconstruct safe mobile heights instead of letting RGL squish them proportionally
            const isChart = item.h >= 20 || item.w >= 20
            const w = isChart ? 20 : 10
            const h = isChart ? 10 : 10

            if (currentX + w > 20) {
                currentX = 0
                currentY += 10 // push down, all default widgets use essentially 10 height on mobile
            }
            const mapped = {
                ...item,
                x: currentX, y: currentY,
                w, h
            }
            currentX += w
            return mapped
        })
    } else if (guaranteedLayouts.xs) {
        // Auto-migrate existing 10-column mobile layouts to 20-column
        const isOld10Col = !guaranteedLayouts.xs.some((item: any) => item.x + item.w > 10)
        if (isOld10Col) {
            guaranteedLayouts.xs = guaranteedLayouts.xs.map((item: any) => ({
                ...item,
                x: item.x * 2,
                w: item.w * 2
            }))
        }
    }

    // Also ensure xs layout has entries for all active widgets
    if (guaranteedLayouts.xs) {
        const xsItems = guaranteedLayouts.xs
        const xsIds = new Set(xsItems.map((item: any) => item.i))
        let maxXsY = xsItems.reduce((max: number, l: any) => Math.max(max, l.y + l.h), 0)
        const missingXsItems: any[] = []
        activeWidgets.forEach(id => {
            if (!xsIds.has(id)) {
                missingXsItems.push({ i: id, x: 0, y: maxXsY, w: 20, h: 10 })
                maxXsY += 10
            }
        })
        if (missingXsItems.length > 0) {
            guaranteedLayouts.xs = [...xsItems, ...missingXsItems]
        }
    }

    // When not editing, mark every item as static to prevent any movement
    const effectiveLayouts = isEditing
        ? guaranteedLayouts
        : Object.fromEntries(
            Object.entries(guaranteedLayouts).map(([bp, items]) => [
                bp,
                items.map((item: any) => ({
                    ...item,
                    static: true,
                    isDraggable: false,
                    isResizable: false
                }))
            ])
        )

    let containerClass = `widget-grid-container ${isEditing ? 'editing' : ''}`
    let containerStyle: React.CSSProperties = {}

    if (forcedLayout === 'desktop') {
        containerClass += ' force-desktop'
        containerStyle = { minWidth: '1000px' }
    } else if (forcedLayout === 'mobile') {
        containerClass += ' force-mobile'
    }

    // Determine the breakpoints based on forced layout logic
    const activeBreakpoints = forcedLayout === 'mobile'
        ? { lg: 99999, xs: 0 }
        : forcedLayout === 'desktop'
            ? { lg: 0, xs: -1 }
            : { lg: 768, xs: 0 }

    return (
        <>
            <div style={{ overflowX: 'auto', width: '100%', paddingBottom: '20px' }}>
                <div className={containerClass} ref={containerRef} style={containerStyle}>
                    <Responsive
                        key={layoutVersion}
                        width={width}
                        className="layout"
                        layouts={effectiveLayouts}
                        breakpoints={activeBreakpoints}
                        cols={{ lg: 40, xs: 20 }}
                        rowHeight={10}
                        onLayoutChange={handleLayoutChange}
                        onDragStart={handleDragStart}
                        onResizeStart={handleResizeStart}
                        isDraggable={isEditing}
                        isResizable={isEditing}
                        isBounded={true}
                        draggableCancel=".widget-controls, .widget-controls *, .widget-settings-popover, .widget-settings-popover *"
                        {...({
                            compactType: 'vertical',
                            useCSSTransforms: true,
                            margin: [10, 10]
                        } as any)}
                    >
                        {activeWidgets.map((widgetId) => {
                            const ws = widgetSettings[widgetId]

                            return (
                                <div key={widgetId} className={`widget-grid-item rgl-item ${isEditing ? 'is-editing' : ''} ${settingsOpen === widgetId ? 'settings-open' : ''}`}>
                                    {/* Edit controls overlay */}
                                    {isEditing && (
                                        <div className="widget-controls" onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
                                            <button className="widget-ctrl-btn"
                                                title="Settings"
                                                onTouchEnd={e => { e.stopPropagation(); e.preventDefault(); setSettingsOpen(settingsOpen === widgetId ? null : widgetId) }}
                                                onClick={e => { e.stopPropagation(); setSettingsOpen(settingsOpen === widgetId ? null : widgetId) }}>
                                                ⚙
                                            </button>
                                            <button className="widget-ctrl-btn remove"
                                                title="Remove"
                                                onTouchEnd={e => { e.stopPropagation(); e.preventDefault(); removeWidget(widgetId) }}
                                                onClick={e => { e.stopPropagation(); removeWidget(widgetId) }}>
                                                ✕
                                            </button>
                                        </div>
                                    )}

                                    {/* Widget settings popover */}
                                    {isEditing && settingsOpen === widgetId && (
                                        <div className="widget-settings-popover" onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
                                            {KPI_ACCENTS[widgetId] && !KPI_NO_COMPARISONS.has(widgetId) ? (
                                                <>
                                                    <label>
                                                        <input type="checkbox" checked={ws?.showLastMonth !== false}
                                                            onChange={e => updateWidgetSettings(widgetId, { showLastMonth: e.target.checked })} />
                                                        Show prior month
                                                    </label>
                                                    <label>
                                                        <input type="checkbox" checked={ws?.showLastYear !== false}
                                                            onChange={e => updateWidgetSettings(widgetId, { showLastYear: e.target.checked })} />
                                                        Show prior year
                                                    </label>
                                                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--color-border)' }}>
                                                        <label>
                                                            <input type="checkbox" checked={ws?.showPctChange !== false}
                                                                onChange={e => updateWidgetSettings(widgetId, { showPctChange: e.target.checked })} />
                                                            Show % change
                                                        </label>
                                                        <label>
                                                            <input type="checkbox" checked={ws?.showNetChange === true}
                                                                onChange={e => updateWidgetSettings(widgetId, { showNetChange: e.target.checked })} />
                                                            Show net change
                                                        </label>
                                                    </div>
                                                </>
                                            ) : widgetId === 'dailyChart' ? (
                                                <>
                                                    <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 'var(--text-xs)' }}>Compare to:</div>
                                                    <select
                                                        value={ws?.chartCompareTo || 'lastMonth'}
                                                        onChange={e => updateWidgetSettings(widgetId, { chartCompareTo: e.target.value as any })}
                                                        style={{ width: '100%', marginBottom: 8, padding: 4, fontSize: 'var(--text-xs)', borderRadius: 'var(--radius-sm)', background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)' }}
                                                    >
                                                        <option value="none">None</option>
                                                        <option value="lastMonth">Last Month</option>
                                                        <option value="lastYear">Last Year</option>
                                                    </select>
                                                    <label>
                                                        <input type="checkbox" checked={ws?.showDiscounts !== false}
                                                            onChange={e => updateWidgetSettings(widgetId, { showDiscounts: e.target.checked })} />
                                                        Show discounts
                                                    </label>
                                                </>
                                            ) : widgetId === 'salesByStore' ? (
                                                <>
                                                    <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 'var(--text-xs)' }}>Sort by:</div>
                                                    <select
                                                        value={ws?.sortBy || 'performance'}
                                                        onChange={e => updateWidgetSettings(widgetId, { sortBy: e.target.value as any })}
                                                        style={{ width: '100%', marginBottom: 8, padding: 4, fontSize: 'var(--text-xs)', borderRadius: 'var(--radius-sm)', background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)' }}
                                                    >
                                                        <option value="performance">Performance</option>
                                                        <option value="name">Store Name</option>
                                                        <option value="number">Store Number</option>
                                                    </select>
                                                    <label>
                                                        <input type="checkbox" checked={ws?.showDiscounts !== false}
                                                            onChange={e => updateWidgetSettings(widgetId, { showDiscounts: e.target.checked })} />
                                                        Show discounts
                                                    </label>
                                                </>
                                            ) : (
                                                <span style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-xs)' }}>
                                                    No configurable settings for this widget.
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Widget content */}
                                    <div className="widget-body">
                                        <DelayedLoadingWrapper isFetching={isFetching}>
                                            {renderWidget(widgetId)}
                                        </DelayedLoadingWrapper>
                                    </div>
                                </div>
                            )
                        })}
                    </Responsive>
                </div>
            </div>


        </>
    )
}
